import { FC, ReactNode, useEffect, useRef, useState } from "react"
import { useLocation, useNavigate } from "react-router"
import { useAuth } from "../../../../components/auth"
import {
  ActivityIcon,
  ContentIcon,
  DashboardIcon,
  LogoIcon,
  LogoutIcon,
  ProfileIcon,
  RolesIcon,
  TeamIcon,
} from "../../../../icons"
import NavItem from "./components/NavItem"
import "./index.less"

const links: { [key: string]: string } = {
  ["DSC"]: "dsc",
  ["More"]: "more",
  ["Links"]: "links",
  ["Posts"]: "posts",
  ["Salon"]: "salon",
  ["Region"]: "region",
  ["Events"]: "events",
  ["Assets"]: "assets",
  ["Products"]: "products",
  ["Analytics"]: "analytics",
  ["Guest Roles"]: "guest-roles",
  ["Profile"]: "profile/account",
  ["Leaderboard"]: "leaderboard",
  ["Competition"]: "competition",
  ["Admin Roles"]: "admin-roles",
  ["Activity Log"]: "activity-log",
  ["Notifications"]: "notifications",
  ["Sales Manager"]: "sales-manager",
  ["Distributor"]: "distributor/users",
}

const BASE_ICONS = {
  ["Team"]: <TeamIcon />,
  ["Roles"]: <RolesIcon />,
  ["Log Out"]: <LogoutIcon />,
  ["Content"]: <ContentIcon />,
  ["Dashboard"]: <DashboardIcon />,
  ["Activity Log"]: <ActivityIcon />,
}

const Navigation: FC = () => {
  const { avatar, permissions } = useAuth()

  const navigate = useNavigate()

  const [openedElement, setOpenedElement] = useState<string>()
  const [isHidden, setIsHidden] = useState(false)

  const firstMenuRef = useRef<HTMLElement>(null)
  const secondMenuRef = useRef<HTMLElement>(null)

  const [secondShift, setSecondShift] = useState(0)

  const { pathname } = useLocation()

  const icons: { [key: string]: ReactNode } = {
    ...BASE_ICONS,
    ["Profile"]: avatar ? <img src={avatar} alt={"avatar"} /> : <ProfileIcon />,
  }

  const handleElementClick = (parent: string, child: string) => {
    navigate(`/account/${parent}/${child}`)
  }

  const handleStyle = (parent: string, child: string) => {
    if (pathname?.includes(`/account/${parent}/${child.split("/").slice(0, 1)[0]}`)) {
      return { backgroundColor: "#677685", color: "#fff" }
    }
    return {}
  }

  useEffect(() => {
    if (!firstMenuRef.current || !secondMenuRef.current) return

    const observer = new ResizeObserver(() => {
      requestAnimationFrame(() => {
        const first = firstMenuRef.current?.getBoundingClientRect()
        const second = secondMenuRef.current?.getBoundingClientRect()

        if (!first || !second) return

        if (first.bottom + 10 > second.top) {
          const calculatedShift = first.bottom - second.top + 20

          if (second.bottom + calculatedShift > window.innerHeight) {
            setIsHidden(true)
            setSecondShift(0)
          } else {
            setIsHidden(false)
            setSecondShift(calculatedShift)
          }
        } else {
          setIsHidden(false)
          setSecondShift(0)
        }
      })
    })

    observer.observe(firstMenuRef.current)
    return () => observer.disconnect()
  }, [])

  const generateBlocks = () => (
    <>
      <nav ref={firstMenuRef} className={"first-menu"}>
        {permissions?.firstMenu.map(item => (
          <NavItem
            key={item.name}
            name={item.name}
            link={links[item.name]}
            label={icons[item.name]}
            openedElement={openedElement}
            setOpenedElement={setOpenedElement}
            children={
              item?.blocks
                ? item.blocks.map((block: PermissionBlockInterface) => (
                    <div
                      key={links[block?.name]}
                      className={"nav-subitem"}
                      onClick={() => handleElementClick(item.name.toLowerCase(), links[block?.name])}
                      style={handleStyle(item.name.toLowerCase(), links[block.name])}
                    >
                      {block.name}
                    </div>
                  ))
                : null
            }
          />
        ))}
      </nav>

      <nav
        ref={secondMenuRef}
        className={`second-menu`}
        style={{
          transform: `translateY(${secondShift}px)`,
          opacity: isHidden ? 0 : 1,
          visibility: isHidden ? "hidden" : "visible",
          transitionDuration: "300",
        }}
      >
        {permissions?.secondMenu?.map(item => (
          <NavItem
            key={item.name}
            name={item.name}
            link={links[item.name]}
            label={icons[item.name]}
            openedElement={openedElement}
            setOpenedElement={setOpenedElement}
          />
        ))}
      </nav>
    </>
  )

  return (
    <section className={"navigation"}>
      <LogoIcon className={"logo"} />
      {generateBlocks()}
    </section>
  )
}

export default Navigation
